import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { usePublicClient } from 'wagmi';
import { ethers } from 'ethers';
import { TOKEN_LIST } from '../config/tokens';
import { useTokenDetector } from '../hooks/useTokenDetector';
import { useFavoriteTokens } from '../hooks/useFavoriteTokens';
import { customTokenStorage } from '../utils/customTokenStorage';
import '../App.css';

const TokenModal = ({ isOpen, onClose, onSelect, selectedToken }) => {
  const [search, setSearch] = useState('');
  const [customTokens, setCustomTokens] = useState([]);
  const [coingeckoResults, setCoingeckoResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectionError, setDetectionError] = useState('');
  const [mainTokensPrices, setMainTokensPrices] = useState({});
  
  const publicClient = usePublicClient();
  const { detectToken } = useTokenDetector();
  const { favorites, isFavorite, toggleFavorite } = useFavoriteTokens();

  // Load custom tokens on mount
  useEffect(() => {
    if (isOpen) {
      setCustomTokens(customTokenStorage.getAll());
      setSearch('');
      setDetectionError('');
    }
  }, [isOpen]);

  // OPTIMIZED: Fetch prices INSTANTLY when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchMainTokensPrices = async () => {
      try {
        const tokenAddresses = {
          'WBNB': '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c',
          'BNB': '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c',
          'BTCB': '0x7130d2A12B9BCbFAe4f2634d864A1Ee1Ce3Ead9c',
          'ETH': '0x2170Ed0880ac9A755fd29B2688956BD959F933F8',
          'LRST': '0x6421355286Ecb21Ad502582Cd0B0DA16971D5F45'
        };
        
        // Stablecoins - instant display
        const prices = {
          'USDT': { priceUsd: '1.00', priceChange24h: 0 },
          'USDC': { priceUsd: '1.00', priceChange24h: 0 }
        };
        
        // AGGRESSIVE PARALLEL FETCH - Use Promise.allSettled for speed
        const fetchPromises = Object.entries(tokenAddresses).map(async ([symbol, address]) => {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s timeout
            
            const response = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${address}`, { 
              cache: 'no-store',
              signal: controller.signal
            });
            clearTimeout(timeoutId);
            
            const data = await response.json();
            
            if (data && data.pairs && data.pairs.length > 0) {
              const bestPair = symbol === 'LRST' 
                ? data.pairs[0]
                : data.pairs
                    .filter(p => p.chainId === 'bsc' && p.liquidity?.usd > 10000)
                    .sort((a, b) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0))[0];
              
              if (bestPair && bestPair.priceUsd) {
                const targetSymbol = symbol === 'WBNB' ? 'BNB' : symbol;
                const priceChange = bestPair.priceChange?.h24 ?? bestPair.priceChange24h ?? 0;
                
                return {
                  symbol: targetSymbol,
                  data: {
                    priceUsd: bestPair.priceUsd,
                    priceChange24h: parseFloat(priceChange)
                  }
                };
              }
            }
          } catch (err) {
            // Silent fail, no console spam
          }
          return null;
        });
        
        // Use allSettled to get results even if some fail
        const results = await Promise.allSettled(fetchPromises);
        
        // Update prices as they arrive
        results.forEach(result => {
          if (result.status === 'fulfilled' && result.value) {
            prices[result.value.symbol] = result.value.data;
            if (result.value.symbol === 'BNB') {
              prices['WBNB'] = result.value.data;
            }
          }
        });
        
        setMainTokensPrices(prices);
      } catch (error) {
        console.error('Failed to fetch main tokens prices:', error);
      }
    };

    // INSTANT fetch on modal open
    fetchMainTokensPrices();
    
    // 10-second interval for real-time updates
    const interval = setInterval(fetchMainTokensPrices, 10000);
    
    return () => clearInterval(interval);
  }, [isOpen]);

  // Format price based on value - STRICT: 2 decimals for high-value, max 8 for cheap
  const formatPrice = (priceStr) => {
    const price = parseFloat(priceStr);
    
    if (isNaN(price) || price === 0) return '$0.00';
    
    // HIGH-VALUE TOKENS (>= $1): Always show EXACTLY 2 decimals
    // Examples: BNB $602.01, ETH $3,240.50, BTCB $71,500.00
    if (price >= 1) {
      return `$${price.toLocaleString(undefined, { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      })}`;
    }
    
    // CHEAP TOKENS (< $1): Show up to 8 decimals maximum, remove trailing zeros
    // Examples: $0.00000001, $0.0000087, $0.12345678
    const formatted = price.toFixed(8).replace(/\.?0+$/, '');
    
    // Ensure we have at least some decimal places for very small numbers
    if (formatted.indexOf('.') === -1) {
      return `$${formatted}.00`;
    }
    
    return `$${formatted}`;
  };

  // Check if search is a valid address and auto-detect
  useEffect(() => {
    const detectFromAddress = async () => {
      if (!search || !ethers.isAddress(search)) {
        setDetectionError('');
        return;
      }

      // Check if already in default list or custom tokens
      const allTokens = [...TOKEN_LIST, ...customTokens];
      const existing = allTokens.find(
        t => t.address.toLowerCase() === search.toLowerCase()
      );
      
      if (existing) {
        setDetectionError('');
        return;
      }

      // Auto-detect new token
      setIsDetecting(true);
      setDetectionError('');
      
      try {
        const provider = new ethers.BrowserProvider(publicClient);
        const token = await detectToken(search, provider);
        
        if (token) {
          // Add to custom tokens
          const updated = customTokenStorage.add(token);
          setCustomTokens(updated);
          setDetectionError('');
        } else {
          setDetectionError('Token not found or invalid');
        }
      } catch (err) {
        setDetectionError('Failed to detect token');
      } finally {
        setIsDetecting(false);
      }
    };

    const timer = setTimeout(detectFromAddress, 500);
    return () => clearTimeout(timer);
  }, [search, detectToken, publicClient, customTokens]);

  // Search CoinGecko when user types - simplified approach
  useEffect(() => {
    const searchCoingecko = async () => {
      if (!search || search.length < 2 || ethers.isAddress(search)) {
        setCoingeckoResults([]);
        return;
      }

      setIsSearching(true);
      try {
        console.log('[CoinGecko] Searching:', search);

        // Use simple search endpoint - more reliable
        const response = await fetch(
          `https://api.coingecko.com/api/v3/search?query=${encodeURIComponent(search)}`,
          {
            headers: {
              'Accept': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        console.log('[CoinGecko] Results:', data.coins?.length || 0);

        if (!data.coins || data.coins.length === 0) {
          setCoingeckoResults([]);
          return;
        }

        // Get top 5 results
        const topCoins = data.coins.slice(0, 5);
        
        // Fetch each coin detail individually with error handling
        const bscTokens = [];
        for (const coin of topCoins) {
          try {
            const detailResponse = await fetch(
              `https://api.coingecko.com/api/v3/coins/${coin.id}?localization=false&tickers=false&market_data=false&community_data=false&developer_data=false`
            );
            
            if (detailResponse.ok) {
              const detail = await detailResponse.json();
              
              // Check if has BSC address
              if (detail.platforms && detail.platforms['binance-smart-chain']) {
                const bscAddress = detail.platforms['binance-smart-chain'];
                if (bscAddress && bscAddress.startsWith('0x')) {
                  // CRITICAL FIX: Fetch actual decimals from contract instead of assuming 18
                  let tokenDecimals = 18; // Default fallback
                  try {
                    const decimalsResult = await publicClient.readContract({
                      address: bscAddress,
                      abi: [{
                        name: 'decimals',
                        type: 'function',
                        stateMutability: 'view',
                        inputs: [],
                        outputs: [{ type: 'uint8' }],
                      }],
                      functionName: 'decimals',
                    });
                    tokenDecimals = Number(decimalsResult);
                    console.log(`[CoinGecko] ${detail.symbol} decimals:`, tokenDecimals);
                  } catch (err) {
                    console.warn(`[CoinGecko] Could not fetch decimals for ${detail.symbol}, using 18`);
                  }
                  
                  bscTokens.push({
                    symbol: detail.symbol.toUpperCase(),
                    name: detail.name,
                    address: bscAddress,
                    decimals: tokenDecimals, // Use fetched decimals
                    isNative: false,
                    logo: detail.image?.small || detail.image?.thumb || coin.thumb,
                    coingeckoId: detail.id,
                    isFromCoingecko: true
                  });
                  console.log(`[CoinGecko] Added: ${detail.symbol} - ${bscAddress} (${tokenDecimals} decimals)`);
                }
              }
            }
          } catch (err) {
            console.warn(`[CoinGecko] Skip ${coin.symbol}:`, err.message);
            continue;
          }
        }
        
        console.log('[CoinGecko] BSC tokens found:', bscTokens.length);
        setCoingeckoResults(bscTokens);

      } catch (error) {
        console.error('[CoinGecko] Error:', error);
        setCoingeckoResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(searchCoingecko, 1000);
    return () => clearTimeout(debounceTimer);
  }, [search]);

  const handleDeleteCustomToken = (address, e) => {
    e.stopPropagation();
    const updated = customTokenStorage.remove(address);
    setCustomTokens(updated);
  };

  // Combine default and custom tokens
  const allTokens = [...TOKEN_LIST, ...customTokens];
  
  const filteredTokens = allTokens.filter((token) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      token.symbol.toLowerCase().includes(q) ||
      token.name.toLowerCase().includes(q) ||
      token.address.toLowerCase().includes(q)
    );
  });

  // Sort: Favorites first, then alphabetically
  const sortedFilteredTokens = [...filteredTokens].sort((a, b) => {
    const aIsFav = isFavorite(a.address);
    const bIsFav = isFavorite(b.address);
    
    if (aIsFav && !bIsFav) return -1;
    if (!aIsFav && bIsFav) return 1;
    return a.symbol.localeCompare(b.symbol);
  });

  // Combine filtered tokens with CoinGecko results
  const displayTokens = search && coingeckoResults.length > 0 
    ? [...sortedFilteredTokens, ...coingeckoResults]
    : sortedFilteredTokens;

  return createPortal(
    <motion.div
      className="lr-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: "easeInOut" }}
      onClick={onClose}
    >
      <motion.div 
        className="lr-modal-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
      />
      <motion.div
        className="lr-modal-content"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ 
          duration: 0.25, 
          ease: [0.4, 0, 0.2, 1] // cubic-bezier for smooth professional feel
        }}
      >
            <div className="lr-modal-header">
              <h3>Select a Token</h3>
              <button className="lr-modal-close" onClick={onClose} data-press>
                ✕
              </button>
            </div>

            {/* Tabs - REMOVED */}

            <div className="lr-modal-body">
              <div className="lr-token-search">
                {/* Search Input */}
                <input
                  className="lr-input lr-input-search"
                  type="text"
                  placeholder="Search name or paste address"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {isDetecting && (
                  <div className="lr-helper-text" style={{ marginTop: '6px', color: '#4caf50' }}>
                    🔍 Detecting token...
                  </div>
                )}
                {detectionError && (
                  <div className="lr-helper-text" style={{ marginTop: '6px', color: '#ff5252' }}>
                    ⚠️ {detectionError}
                  </div>
                )}
              </div>

              <div className="lr-token-list-wrapper">
                <ul className="lr-token-list">
                  {displayTokens.map((token) => (
                    <motion.li
                      key={token.address}
                      className="lr-token-list-item"
                      onClick={() => onSelect(token)}
                      whileHover={{ x: 4 }}
                      whileTap={{ scale: 0.98 }}
                      style={{ position: 'relative' }}
                    >
                      <img 
                        src={token.logo} 
                        alt={token.symbol} 
                        className="lr-token-logo"
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${token.symbol}&background=e50914&color=fff`;
                        }}
                      />
                      <div className="lr-token-list-text" style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '14px' }}>{token.symbol}</div>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>{token.name}</div>
                        {token.isFromCoingecko && (
                          <span style={{ fontSize: '10px', color: '#999', marginTop: '2px', display: 'block' }}>
                            via CoinGecko
                          </span>
                        )}
                      </div>
                      
                      {/* Price Info */}
                      {mainTokensPrices[token.symbol] && (
                        <div style={{ textAlign: 'right', marginRight: '8px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: '#fff' }}>
                            {formatPrice(mainTokensPrices[token.symbol].priceUsd)}
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: mainTokensPrices[token.symbol].priceChange24h >= 0 ? '#4caf50' : '#f44336' 
                          }}>
                            {mainTokensPrices[token.symbol].priceChange24h >= 0 ? '+' : ''}
                            {parseFloat(mainTokensPrices[token.symbol].priceChange24h).toFixed(2)}%
                          </div>
                        </div>
                      )}
                      
                      {/* Favorite Star Button */}
                      <button
                        className="lr-token-favorite-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(token);
                        }}
                        style={{
                          marginLeft: 'auto',
                          marginRight: '8px',
                          padding: '4px',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: isFavorite(token.address) ? '#ffd700' : 'rgba(255,255,255,0.3)',
                          fontSize: '18px',
                          transition: 'all 0.2s ease'
                        }}
                        title={isFavorite(token.address) ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        {isFavorite(token.address) ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                          </svg>
                        )}
                      </button>
                      
                      {token.isCustom && (
                        <button
                          className="lr-token-delete-btn"
                          onClick={(e) => handleDeleteCustomToken(token.address, e)}
                          style={{
                            padding: '4px 8px',
                            fontSize: '11px',
                            borderRadius: '6px',
                            border: '1px solid rgba(255,82,82,0.3)',
                            background: 'rgba(255,82,82,0.1)',
                            color: '#ff5252',
                            cursor: 'pointer'
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </motion.li>
                  ))}
                </ul>
                {isSearching && (
                  <div className="lr-token-list-empty">
                    Searching CoinGecko...
                  </div>
                )}
                {displayTokens.length === 0 && !isDetecting && !isSearching && (
                  <div className="lr-token-list-empty">
                    {ethers.isAddress(search) 
                      ? 'Detecting token...' 
                      : 'No tokens found. Try searching or paste a token address.'}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>,
        document.body
      );
};

export default TokenModal;
